// Aliased because the new File API collides with the DOM `File` global.
import { File as FSFile } from "expo-file-system";
import type { ImageManipulatorContext } from "expo-image-manipulator";
import { ImageManipulator, SaveFormat } from "expo-image-manipulator";

import type { ImageCompressionSource } from "@/constants/analytics/Analytics";
import {
  FileSystemEvent,
  ImageCompressionField,
} from "@/constants/analytics/Analytics";
import { DocumentExtensions } from "@/constants/Documents";
import { resolveHeicUri } from "@/hooks/useProprietaryHeicFiles/resolveHeicUri";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { getSMTImageMimeType } from "@/utils/documents.util";
import { getFileNameFromURI } from "@/utils/filePicker.util";
import { isWeb } from "@/utils/platform.util";

const DefaultImageCompressionOption = {
  CompressionRate: 0.8,
  Format: SaveFormat.JPEG,
};

interface CompressImageParams {
  compressionRate?: number;
  format?: SaveFormat;
  originalAsset: FilePickerAsset;
  source: ImageCompressionSource;
}

interface UseImageCompression {
  compressImage: (params: CompressImageParams) => Promise<FilePickerAsset>;
}

// expo-modules-core types registerWebModule's return as the class, not an
// instance, so TS can't see `manipulate` (it exists and works at runtime).
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/naming-convention -- mistyped upstream; assert the real instance shape; PascalCase ImageManipulator is the imported value
const imageManipulator = ImageManipulator as unknown as {
  manipulate: (uri: string) => ImageManipulatorContext;
};

interface GetFileSizeParams {
  file?: File;
  uri: string;
}
const getFileSize = async ({
  file,
  uri,
}: GetFileSizeParams): Promise<number> => {
  // The picker hands us the blob directly for the original asset on web.
  if (file) {
    return file.size;
  }

  // On web the uri is an in-memory `blob:` URL (e.g. the compressor output) with
  // no `file`. Fetching it resolves from memory (no network), so we can read the
  // resulting Blob's size. Size is analytics-only, so swallow failures rather
  // than let them break compression.
  if (isWeb()) {
    try {
      const blob = await fetch(uri).then((response) => response.blob());
      return blob.size;
    } catch {
      return 0;
    }
  }

  // Native: uri is a real file path readable by expo-file-system. `size` is 0
  // when the file does not exist or cannot be read.
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- web .d.ts stub omits size, present on native at runtime
  const nativeFile = new FSFile(uri) as unknown as { size: number };
  return nativeFile.size;
};

const getFileSizeInKB = (sizeInBytes: number): string => {
  return (sizeInBytes / 1024).toFixed(2);
};

// On native the compressor's uri ends in `.jpg`; on web it's a `blob:` URL whose
// last segment is a bare UUID. Append the format extension only when the name
// lacks an image one, so the upload's `filename` is well-formed on both
// platforms (and a native `.jpg` isn't turned into `.jpg.jpeg`). `format` is
// included so a target like webp (absent from DocumentExtensions) still counts.
const getCompressedFileName = ({
  format,
  uri,
}: {
  format: SaveFormat;
  uri: string;
}): string => {
  const fileName = getFileNameFromURI(uri);
  const lowerCaseName = fileName.toLowerCase();
  const imageExtensions = [...DocumentExtensions.Image, format];
  const hasImageExtension = imageExtensions.some((extension) =>
    lowerCaseName.endsWith(`.${extension}`),
  );
  return hasImageExtension ? fileName : `${fileName}.${format}`;
};

const useImageCompression = (): UseImageCompression => {
  const { track } = useAnalytics();
  const { captureException } = useAppMonitor();

  const compressImage = async ({
    compressionRate = DefaultImageCompressionOption.CompressionRate,
    format = DefaultImageCompressionOption.Format,
    originalAsset,
    source,
  }: CompressImageParams): Promise<FilePickerAsset> => {
    const originalUri = originalAsset.uri;
    const originalSizeInBytes = await getFileSize({
      file: originalAsset.file,
      uri: originalUri,
    });
    const originalSizeKB = getFileSizeInKB(originalSizeInBytes);
    const initialTimestamp = Date.now();
    const originalMediaType = originalAsset.mimeType;

    try {
      // on mobile, expo-image-manipulator handles HEIC files and successfully converts them to .jpg (and resolveHeicUri.native is a no-op)
      // on web, expo-image-manipulator can't decode HEIC directly (non-Safari browsers can't view HEIC files), so here pre-convert to .jpg via heic-to first
      const manipulatableUri = await resolveHeicUri({
        contentType: originalMediaType,
        uri: originalUri,
      });

      // Contextual ImageManipulator API (Expo SDK 53+). Works on web, where it
      // produces a `blob:` URI consumed by the web upload path.
      const context = imageManipulator.manipulate(manipulatableUri);
      // eslint-disable-next-line testing-library/render-result-naming-convention -- false positive: this is ImageManipulator.renderAsync(), not a Testing Library render
      const image = await context.renderAsync();
      const imageResult = await image.saveAsync({
        compress: compressionRate,
        format,
      });
      context.release();
      image.release();

      const compressionDuration = Date.now() - initialTimestamp;
      // The compressed result has no `file`; on web its uri is a `blob:` URL.
      const compressedSizeInBytes = await getFileSize({
        uri: imageResult.uri,
      });
      const compressedSizeKB = getFileSizeInKB(compressedSizeInBytes);
      const compressedMediaType = getSMTImageMimeType(format);

      track(FileSystemEvent.ImageCompressed, {
        [ImageCompressionField.CompressedMediaType]: compressedMediaType,
        [ImageCompressionField.CompressedSizeKB]: compressedSizeKB,
        [ImageCompressionField.CompressionDurationMs]: compressionDuration,
        [ImageCompressionField.CompressionRate]: compressionRate,
        [ImageCompressionField.OriginalMediaType]: originalMediaType,
        [ImageCompressionField.OriginalSizeKB]: originalSizeKB,
        [ImageCompressionField.Source]: source,
      });

      return {
        fileName: getCompressedFileName({ format, uri: imageResult.uri }),
        mimeType: getSMTImageMimeType(format),
        size: compressedSizeInBytes,
        uri: imageResult.uri,
      };
    } catch (error) {
      captureException(new Error("Image compression failed", { cause: error }));
      return originalAsset;
    }
  };
  return { compressImage };
};

export { getCompressedFileName, getFileSize, getFileSizeInKB };
export default useImageCompression;
